import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { api } from '../../services/client';
import { useTranslation } from '../../i18n';
import {
  ICategory,
  IRoomMember,
  ISpace,
  ISpaceDetail,
  ISpaceWithStats,
  ISpaceListStats,
  IPaginated,
} from '../../types';

// --- Interfaces ---

export interface ISpaceListParams {
  page: number;
  search: string;
  limit: number;
}

export interface ICreateSpaceData {
  name: string;
  description: string;
  visibility: string;
  joinRule?: string;
  ownerId: string;
  verificationUrl?: string;
  requireVerification?: boolean;
}

export interface IUpdateSpaceData {
  name?: string;
  description?: string;
  rules?: string;
  visibility?: string;
  joinRule?: string;
  categoryId?: string;
}

export interface IManageSpaceMemberData {
  spaceId: string;
  userIds: string[];
}

// --- Query Key Factory ---

export const spaceKeys = {
  all: ['spaces'] as const,
  lists: () => [...spaceKeys.all, 'list'] as const,
  list: (params: ISpaceListParams) => [...spaceKeys.lists(), params] as const,
  details: () => [...spaceKeys.all, 'detail'] as const,
  detail: (id: string) => [...spaceKeys.details(), id] as const,
};

// --- Helpers ---

const buildQueryString = (params: ISpaceListParams): string => {
  const searchParams = new URLSearchParams();

  if (params.search) searchParams.append('search', params.search);
  searchParams.append('page', String(params.page));
  searchParams.append('limit', String(params.limit));

  return searchParams.toString();
};

// --- Query Hooks ---

export const useSpaceListStats = () =>
  useQuery({
    queryKey: ['space-list-stats'] as const,
    queryFn: () => api.get<ISpaceListStats>('/backoffice/space-list-stats'),
  });

export const useSpaces = (params: ISpaceListParams) =>
  useQuery({
    queryKey: spaceKeys.list(params),
    queryFn: () =>
      api.get<IPaginated<ISpaceWithStats>>(
        `/backoffice/spaces-with-stats?${buildQueryString(params)}`,
      ),
    placeholderData: (previousData) => previousData,
  });

export const useSpaceDetail = (id: string) =>
  useQuery({
    queryKey: spaceKeys.detail(id),
    queryFn: () => api.get<ISpaceDetail>(`/backoffice/spaces/${id}/detail`),
    enabled: !!id,
  });

// --- Mutation Hooks ---

export const useCreateSpace = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (data: ICreateSpaceData) =>
      api.post<ISpace>('/backoffice/spaces', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: spaceKeys.lists() });
      toast.success(t('toast.space_created'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('toast.space_create_failed'));
    },
  });
};

export const useUpdateSpace = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: IUpdateSpaceData }) =>
      api.patch<ISpace>(`/backoffice/spaces/${id}`, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: spaceKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: spaceKeys.detail(variables.id),
      });
      toast.success(t('toast.space_updated'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('toast.space_update_failed'));
    },
  });
};

export const useDeleteSpace = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ id }: { id: string }) =>
      api.delete<void>(`/backoffice/spaces/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: spaceKeys.lists() });
      toast.success(t('toast.space_deleted'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('toast.space_delete_failed'));
    },
  });
};

export const useManageSpaceMember = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ spaceId, userIds }: IManageSpaceMemberData) =>
      api.patch<void>(`/backoffice/spaces/${spaceId}/admins`, { userIds }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: spaceKeys.detail(variables.spaceId),
      });
      toast.success(t('toast.space_admins_updated'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('toast.space_admins_update_failed'));
    },
  });
};

export const useTransferOwnership = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ spaceId, ownerId }: { spaceId: string; ownerId: string }) =>
      api.patch<void>(`/backoffice/spaces/${spaceId}/owner`, { ownerId }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: spaceKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: spaceKeys.detail(variables.spaceId),
      });
      toast.success(t('toast.ownership_transferred'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('toast.ownership_transfer_failed'));
    },
  });
};

export const useCreateRoom = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({
      spaceId,
      data,
    }: {
      spaceId: string;
      data: {
        name: string;
        description: string;
        isAnnouncement: boolean;
        isPrivate: boolean;
        avatar: string;
        memberIds: string[];
      };
    }) => api.post<void>(`/backoffice/spaces/${spaceId}/rooms`, data),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: spaceKeys.detail(variables.spaceId),
      });
      toast.success(t('toast.room_created'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('toast.room_create_failed'));
    },
  });
};

// --- Category Hooks ---

export const categoryKeys = {
  all: ['categories'] as const,
};

export const useCategories = () =>
  useQuery({
    queryKey: categoryKeys.all,
    queryFn: () => api.get<ICategory[]>('/admin/categories'),
  });

// --- Room-level Hooks ---

export const roomKeys = {
  all: ['rooms'] as const,
  members: (roomId: string) => [...roomKeys.all, roomId, 'members'] as const,
};

export const useRoomMembers = (roomId: string) =>
  useQuery({
    queryKey: roomKeys.members(roomId),
    queryFn: () =>
      api.get<IRoomMember[]>(`/backoffice/rooms/${roomId}/members`),
    enabled: !!roomId,
  });

export const useMuteRoom = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ roomId, muteMode }: { roomId: string; muteMode: string }) =>
      api.post<void>(`/backoffice/rooms/${roomId}/mute`, { muteMode }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: spaceKeys.all });
      toast.success(t('toast.room_mute_updated'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('toast.room_mute_update_failed'));
    },
  });
};

export const useDeleteRoom = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (roomId: string) =>
      api.delete<void>(`/backoffice/rooms/${roomId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: spaceKeys.all });
      toast.success(t('toast.room_deleted'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('toast.room_delete_failed'));
    },
  });
};

export const useAssignRoomAdmin = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ roomId, userIds }: { roomId: string; userIds: string[] }) =>
      api.patch<void>(`/backoffice/rooms/${roomId}/admin`, { userIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: spaceKeys.all });
      queryClient.invalidateQueries({ queryKey: roomKeys.all });
      toast.success(t('toast.room_admins_updated'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('toast.room_admins_update_failed'));
    },
  });
};

export const useAddSpaceMembers = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({
      spaceId,
      userIds,
    }: {
      spaceId: string;
      userIds: string[];
    }) =>
      api.post<{ success: boolean; addedCount: number }>(
        `/backoffice/spaces/${spaceId}/members`,
        { userIds },
      ),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: spaceKeys.lists() });
      queryClient.invalidateQueries({
        queryKey: spaceKeys.detail(variables.spaceId),
      });
      toast.success(t('toast.members_added'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('toast.members_add_failed'));
    },
  });
};

// --- Room Detail Mutations ---

export const useUpdateRoom = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({
      roomId,
      data,
    }: {
      roomId: string;
      data: Record<string, unknown>;
    }) => api.patch<void>(`/backoffice/rooms/${roomId}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: spaceKeys.all });
      queryClient.invalidateQueries({ queryKey: roomKeys.all });
      toast.success(t('toast.room_updated'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('toast.room_update_failed'));
    },
  });
};

export const useConvertRoomVisibility = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({
      roomId,
      isPrivate,
    }: {
      roomId: string;
      isPrivate: boolean;
    }) =>
      api.post<void>(`/backoffice/rooms/${roomId}/convert-visibility`, {
        isPrivate,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: spaceKeys.all });
      queryClient.invalidateQueries({ queryKey: roomKeys.all });
      toast.success(t('common.save_success'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('common.save_failed'));
    },
  });
};

export const useUpdateRoomAdmins = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ roomId, userIds }: { roomId: string; userIds: string[] }) =>
      api.patch<void>(`/backoffice/rooms/${roomId}/admin`, { userIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: spaceKeys.all });
      queryClient.invalidateQueries({ queryKey: roomKeys.all });
      toast.success(t('toast.room_admins_updated'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('toast.room_admins_update_failed'));
    },
  });
};

export const useRemoveRoomMember = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ roomId, userId }: { roomId: string; userId: string }) =>
      api.delete<void>(`/backoffice/rooms/${roomId}/members/${userId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roomKeys.all });
      toast.success(t('toast.member_removed'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('toast.member_remove_failed'));
    },
  });
};

export const useAddRoomMembers = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ roomId, userIds }: { roomId: string; userIds: string[] }) =>
      api.post<void>(`/backoffice/rooms/${roomId}/members`, { userIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roomKeys.all });
      queryClient.invalidateQueries({ queryKey: spaceKeys.all });
      toast.success(t('toast.members_added'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('toast.members_add_failed'));
    },
  });
};

export const useDeleteMessage = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (messageId: string) =>
      api.delete<void>(`/backoffice/messages/${messageId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roomKeys.all });
      toast.success(t('toast.message_deleted'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('toast.message_delete_failed'));
    },
  });
};

export const useRemoveSpaceMember = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({ spaceId, userId }: { spaceId: string; userId: string }) =>
      api.delete<void>(`/backoffice/spaces/${spaceId}/members/${userId}`),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: spaceKeys.detail(variables.spaceId),
      });
      queryClient.invalidateQueries({ queryKey: spaceKeys.lists() });
      toast.success(t('toast.member_removed'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('toast.member_remove_failed'));
    },
  });
};

export const useUpdateSpaceAdmins = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: ({
      spaceId,
      userIds,
    }: {
      spaceId: string;
      userIds: string[];
    }) => api.patch<void>(`/backoffice/spaces/${spaceId}/admins`, { userIds }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: spaceKeys.detail(variables.spaceId),
      });
      toast.success(t('toast.space_admins_updated'));
    },
    onError: (error: Error) => {
      toast.error(error.message || t('toast.space_admins_update_failed'));
    },
  });
};
